'use client';

import CheckIcon from '@mui/icons-material/Check';
import Box from '@mui/material/Box';
import { alpha, type SxProps, type Theme, useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { type ReactNode, useMemo, useState } from 'react';
import { GlassTooltip } from '../GlassTooltip';

/**
 * Stage shape the stepper consumes. Intentionally a structural subset of LoanStageDto
 * so the portal can pass DB rows directly without mapping.
 */
export interface PhasedStepperStage {
  id: string;
  /** Stage name as stored in the DB (e.g. "Application Review"). */
  name: string;
  /** Stage order — used to sort if the list isn't already sorted. */
  order: number;
  /** Optional brand color override (hex) — falls back to phase tint. */
  color?: string | null;
  /** Optional locale-rendered label — preferred over `name` when present. */
  label?: ReactNode;
  /** Optional short caption rendered under the stage dot (e.g. "5d SLA"). */
  caption?: ReactNode;
}

export interface PhasedStepperPhase {
  /** Stable phase id. */
  id: string;
  /** Phase label rendered on the chip and group header. */
  label: ReactNode;
  /**
   * Stage ids that belong to this phase. Order within the array is preserved when the
   * phase expands.
   */
  stageIds: string[];
  /**
   * Optional severity tint for the phase chip + active sub-stepper accent. Default 'neutral'.
   * Use 'warning' / 'error' for watch / default phases to communicate risk.
   */
  tone?: 'primary' | 'success' | 'warning' | 'error' | 'neutral';
}

export interface PhasedStepperProps {
  /** Full stage list (typically all non-terminal stages from `loanStages.list`). */
  stages: PhasedStepperStage[];
  /** Phase grouping — order matters; the leftmost phase chip appears first. */
  phases: PhasedStepperPhase[];
  /** Current stage id. The phase containing it auto-expands. */
  currentStageId?: string | null;
  /**
   * Click handler for a stage dot. If omitted, the stepper is read-only and stage dots
   * are not interactive.
   */
  onStageClick?: (stage: PhasedStepperStage) => void;
  /**
   * Optional predicate to mark a stage as clickable. If omitted, all stages are clickable
   * when `onStageClick` is provided. Useful for forward-only / one-step-at-a-time policies.
   */
  isStageClickable?: (stage: PhasedStepperStage) => boolean;
  /**
   * Optional tooltip text per stage (e.g. "Cannot advance: 2 requirements not met").
   * Receives the stage and the resolved status.
   */
  getStageTooltip?: (stage: PhasedStepperStage, status: 'completed' | 'current' | 'pending') => ReactNode;
  /** Sx overrides on the outer Box. */
  sx?: SxProps<Theme>;
}

interface ResolvedPhase {
  id: string;
  label: ReactNode;
  tone: NonNullable<PhasedStepperPhase['tone']>;
  stages: PhasedStepperStage[];
  completed: number;
  containsCurrent: boolean;
}

function severityColor(theme: Theme, tone: ResolvedPhase['tone']): string {
  if (tone === 'neutral') return theme.palette.text.secondary;
  return theme.palette[tone].main;
}

function chooseInitialPhase(phases: ResolvedPhase[]): string | null {
  const withCurrent = phases.find((p) => p.containsCurrent);
  if (withCurrent) return withCurrent.id;
  // Fall back to the latest in-progress phase (last phase with at least one completed stage).
  const lastWithProgress = [...phases].reverse().find((p) => p.completed > 0);
  if (lastWithProgress) return lastWithProgress.id;
  // Otherwise default to the first phase.
  return phases[0]?.id ?? null;
}

export function PhasedStepper({
  stages,
  phases,
  currentStageId,
  onStageClick,
  isStageClickable,
  getStageTooltip,
  sx,
}: PhasedStepperProps) {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  const { resolvedPhases, currentIndex, sortedStages } = useMemo(() => {
    const sorted = [...stages].sort((a, b) => a.order - b.order);
    const byId = new Map(sorted.map((s) => [s.id, s] as const));
    const currentIdx = currentStageId ? sorted.findIndex((s) => s.id === currentStageId) : -1;

    const resolved: ResolvedPhase[] = phases.map((p) => {
      const phaseStages = p.stageIds.map((id) => byId.get(id)).filter((s): s is PhasedStepperStage => Boolean(s));
      const completed = phaseStages.filter((s) => sorted.findIndex((x) => x.id === s.id) < currentIdx).length;
      const containsCurrent = phaseStages.some((s) => s.id === currentStageId);
      return {
        id: p.id,
        label: p.label,
        tone: p.tone ?? 'neutral',
        stages: phaseStages,
        completed,
        containsCurrent,
      };
    });

    return { resolvedPhases: resolved, currentIndex: currentIdx, sortedStages: sorted };
  }, [stages, phases, currentStageId]);

  const [expandedId, setExpandedId] = useState<string | null>(() => chooseInitialPhase(resolvedPhases));

  // Re-pick expanded phase when currentStageId changes to a different phase.
  const expandedPhase = useMemo(() => {
    if (expandedId == null) return resolvedPhases.find((p) => p.containsCurrent) ?? null;
    return resolvedPhases.find((p) => p.id === expandedId) ?? null;
  }, [expandedId, resolvedPhases]);

  function resolveStageStatus(stage: PhasedStepperStage): 'completed' | 'current' | 'pending' {
    if (stage.id === currentStageId) return 'current';
    const idx = sortedStages.findIndex((s) => s.id === stage.id);
    if (idx >= 0 && idx < currentIndex) return 'completed';
    return 'pending';
  }

  return (
    <Box sx={sx}>
      {/* Phase chip row */}
      <Box
        role="tablist"
        aria-label="Loan lifecycle phases"
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          gap: 1,
          mb: 2,
        }}
      >
        {resolvedPhases.map((phase, index) => {
          const isExpanded = expandedPhase?.id === phase.id;
          const total = phase.stages.length;
          const phaseColor = severityColor(theme, phase.tone);

          return (
            <Box key={phase.id} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box
                component="button"
                type="button"
                role="tab"
                aria-selected={isExpanded}
                aria-controls={`phase-panel-${phase.id}`}
                onClick={() => setExpandedId(phase.id)}
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 0.875,
                  height: 32,
                  px: 1.5,
                  borderRadius: '16px',
                  border: `1px solid ${alpha(phaseColor, isDark ? 0.4 : 0.3)}`,
                  backgroundColor: isExpanded
                    ? alpha(phaseColor, isDark ? 0.2 : 0.14)
                    : alpha(phaseColor, isDark ? 0.08 : 0.06),
                  color: isDark ? theme.palette.common.white : theme.palette.text.primary,
                  fontFamily: 'inherit',
                  cursor: 'pointer',
                  transition: 'background-color 120ms ease, border-color 120ms ease',
                  '&:hover': {
                    backgroundColor: alpha(phaseColor, isDark ? 0.24 : 0.18),
                  },
                  '&:focus-visible': {
                    outline: `2px solid ${phaseColor}`,
                    outlineOffset: 2,
                  },
                }}
              >
                {phase.containsCurrent && (
                  <Box
                    aria-hidden
                    sx={{
                      width: 6,
                      height: 6,
                      borderRadius: '50%',
                      backgroundColor: phaseColor,
                      flexShrink: 0,
                    }}
                  />
                )}
                <Typography
                  component="span"
                  sx={{
                    fontSize: '0.8125rem',
                    fontWeight: 600,
                    lineHeight: 1,
                    fontVariantNumeric: 'tabular-nums',
                  }}
                >
                  {phase.label}
                </Typography>
                <Typography
                  component="span"
                  sx={{
                    fontSize: '0.75rem',
                    fontWeight: 500,
                    color: 'text.secondary',
                    lineHeight: 1,
                    fontVariantNumeric: 'tabular-nums',
                  }}
                >
                  {phase.completed}/{total}
                </Typography>
              </Box>
              {index < resolvedPhases.length - 1 && (
                <Box
                  aria-hidden
                  sx={{
                    width: 12,
                    height: 1,
                    backgroundColor: isDark ? 'rgba(255,255,255,0.16)' : 'rgba(0,0,0,0.12)',
                  }}
                />
              )}
            </Box>
          );
        })}
      </Box>

      {/* Active phase sub-stepper */}
      {expandedPhase && expandedPhase.stages.length > 0 && (
        <Box
          id={`phase-panel-${expandedPhase.id}`}
          role="tabpanel"
          aria-label={typeof expandedPhase.label === 'string' ? `${expandedPhase.label} stages` : undefined}
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 0,
            overflowX: 'auto',
            pb: 1,
          }}
        >
          {expandedPhase.stages.map((stage, idx) => {
            const status = resolveStageStatus(stage);
            const isLast = idx === expandedPhase.stages.length - 1;
            const stageColor = stage.color ?? severityColor(theme, expandedPhase.tone);
            const dotInteractive = Boolean(onStageClick) && (isStageClickable ? isStageClickable(stage) : true);
            const tooltip = getStageTooltip ? getStageTooltip(stage, status) : null;

            const dotBg =
              status === 'completed' ? theme.palette.success.main : status === 'current' ? stageColor : 'transparent';
            const dotBorder =
              status === 'pending'
                ? isDark
                  ? 'rgba(255,255,255,0.24)'
                  : 'rgba(0,0,0,0.2)'
                : status === 'current'
                  ? stageColor
                  : theme.palette.success.main;
            const dotFg =
              status === 'current' ? '#fff' : status === 'completed' ? '#fff' : theme.palette.text.secondary;

            const dot = (
              <Box
                component={dotInteractive ? 'button' : 'div'}
                {...(dotInteractive
                  ? {
                      type: 'button' as const,
                      onClick: () => onStageClick?.(stage),
                    }
                  : {})}
                aria-current={status === 'current' ? 'step' : undefined}
                aria-label={typeof stage.label === 'string' ? stage.label : stage.name}
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 24,
                  height: 24,
                  borderRadius: '50%',
                  border: `2px solid ${dotBorder}`,
                  backgroundColor: dotBg,
                  color: dotFg,
                  cursor: dotInteractive ? 'pointer' : 'default',
                  padding: 0,
                  flexShrink: 0,
                  fontFamily: 'inherit',
                  transition: 'background-color 120ms ease, border-color 120ms ease',
                  '&:hover': dotInteractive ? { borderColor: stageColor } : undefined,
                  '&:focus-visible': {
                    outline: `2px solid ${stageColor}`,
                    outlineOffset: 2,
                  },
                }}
              >
                {status === 'completed' && <CheckIcon sx={{ fontSize: 14 }} />}
                {status === 'current' && (
                  <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: '#fff' }} />
                )}
              </Box>
            );

            return (
              <Box
                key={stage.id}
                sx={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  flex: isLast ? '0 0 auto' : '1 1 0',
                  minWidth: 110,
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 0.5,
                    minWidth: 0,
                    flexShrink: 0,
                  }}
                >
                  {tooltip ? (
                    <GlassTooltip title={tooltip} placement="top">
                      {dot}
                    </GlassTooltip>
                  ) : (
                    dot
                  )}
                  <Typography
                    variant="caption"
                    sx={{
                      color: status === 'current' ? 'text.primary' : 'text.secondary',
                      fontWeight: status === 'current' ? 600 : 500,
                      textAlign: 'center',
                      lineHeight: 1.3,
                      maxWidth: 100,
                      whiteSpace: 'normal',
                      wordBreak: 'break-word',
                    }}
                  >
                    {stage.label ?? stage.name}
                  </Typography>
                  {stage.caption && (
                    <Typography
                      variant="caption"
                      sx={{
                        color: 'text.secondary',
                        fontSize: '0.6875rem',
                        lineHeight: 1.2,
                      }}
                    >
                      {stage.caption}
                    </Typography>
                  )}
                </Box>
                {!isLast && (
                  <Box
                    aria-hidden
                    sx={{
                      flex: 1,
                      height: 2,
                      mt: '11px', // center on the 24px dot
                      mx: 1,
                      borderRadius: 1,
                      backgroundColor:
                        status === 'completed'
                          ? theme.palette.success.main
                          : isDark
                            ? 'rgba(255,255,255,0.12)'
                            : 'rgba(0,0,0,0.1)',
                      minWidth: 24,
                    }}
                  />
                )}
              </Box>
            );
          })}
        </Box>
      )}
    </Box>
  );
}

PhasedStepper.displayName = 'PhasedStepper';

export default PhasedStepper;
